import { FC, MouseEvent, useCallback, useMemo, useRef, useState } from 'react';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Add,
  ContentCopy,
  Delete,
  DragIndicator,
  ViewColumn,
} from '@mui/icons-material';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { FIRM_SUMMARY_CUSTOM_VIEWS_ENABLER } from 'common/constants/features/features';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import {
  addColumnWithDependencies,
  ColType,
  COLUMN_TYPES,
  DEFAULT_FIRM_SUMMARY_COLUMNS,
  Filter,
  FilterOp,
  getAffectedColumns,
  getValueTypesForSummary,
  SortKey,
  SpreadsheetDefinition,
  SummaryDefinition,
  SummaryGroupBy,
} from 'common/types/summaryDefinitions';
import { Tab, TabsNavigation } from 'components';
import useCheckFeatures from 'components/FeatureEnabler/hooks';
import { SummaryDefinitionsProvider, useSummaryDefinitions } from 'context/SummaryDefinitionsContext';
import { FirmPendingDecisions, NoCompanyMessage, NoInformationMessage } from 'pages/Firms/components';
import { useFirmSummaryTab } from 'pages/Firms/hooks';
import { SUPPORTED_FIRM_SUMMARY_COLUMN_TYPES } from 'pages/Firms/hooks/useFirmSummarySheet/config/createColumns';
import { useApproachQueryParam } from 'pages/ValuationsAllocation/hooks';
import { getObjectValue } from 'utilities';

function generateCloneName(sourceName: string, existingNames: string[]): string {
  const baseName = sourceName.replace(/\s*\(Copy(?:\s+\d+)?\)\s*$/i, '').trim();
  const nameSet = new Set(existingNames.map(n => n.toLowerCase()));

  const firstCandidate = `${baseName} (Copy)`;
  if (!nameSet.has(firstCandidate.toLowerCase())) {
    return firstCandidate;
  }

  let counter = 2;
  while (nameSet.has(`${baseName} (Copy ${counter})`.toLowerCase())) {
    counter++;
  }
  return `${baseName} (Copy ${counter})`;
}

const SortableListItem: FC<{
  id: string;
  children: React.ReactNode;
  secondaryAction?: React.ReactNode;
  disabled?: boolean;
}> = ({ id, children, secondaryAction, disabled }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: `${transition}, transform 200ms cubic-bezier(0.25, 0.1, 0.25, 1)`,
    opacity: isDragging ? 0 : 1,
    cursor: isDragging ? 'grabbing' : undefined,
  };

  return (
    <ListItem
      ref={setNodeRef}
      style={style}
      secondaryAction={secondaryAction}
      sx={{
        alignItems: 'flex-start',
        borderRadius: 1,
        transition: 'background-color 150ms ease',
        ...(isDragging && {
          backgroundColor: 'action.hover',
          boxShadow: 2,
        }),
      }}>
      <ListItemIcon
        sx={{
          minWidth: 32,
          alignSelf: 'center',
          cursor: disabled ? 'not-allowed' : 'grab',
          color: 'text.secondary',
          borderRadius: 1,
          transition: 'background-color 150ms ease',
          ...(!disabled && {
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }),
        }}
        {...(disabled ? {} : { ...attributes, ...listeners })}>
        <DragIndicator fontSize="small" />
      </ListItemIcon>
      {children}
    </ListItem>
  );
};

const FirmSummaryTabs: FC = () => {
  const [, checkFeature] = useCheckFeatures();
  const isCustomViewsEnabled = checkFeature(FIRM_SUMMARY_CUSTOM_VIEWS_ENABLER);

  const sortableSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const [activeDragId, setActiveDragId] = useState<string | null>(null);

  const {
    orderedDefinitions,
    activeDefinition,
    setActiveDefinition,
    createDefinition,
    cloneDefinition,
    deleteDefinition,
    updateDefinition,
    refetch,
    isLoading,
    isSaving,
  } = useSummaryDefinitions();
  const { setApproachQueryParam } = useApproachQueryParam();

  // Get the spreadsheet definition from the active definition
  const spreadsheetDefinition = activeDefinition?.spreadsheet_definition ?? activeDefinition?.spreadsheetDefinition;

  // Create firm summary tab with the active definition's columns
  const { firmSummaryTab, hasNoCompanies, hasNoInvestments } = useFirmSummaryTab({ spreadsheetDefinition });
  const [currentTabMenu, setCurrentTabMenu] = useState<HTMLSpanElement | null>(null);
  const [isColumnEditorOpen, setIsColumnEditorOpen] = useState(false);
  const addTabClickedRef = useRef(false);
  const [editorName, setEditorName] = useState('');
  const [editorColumns, setEditorColumns] = useState<ColType[]>([]);
  const [columnToAdd, setColumnToAdd] = useState<ColType | ''>('');
  const [editorGroupBy, setEditorGroupBy] = useState<SummaryGroupBy>('company');
  const [editorSortKeys, setEditorSortKeys] = useState<SortKey[]>([]);
  const [editorFilters, setEditorFilters] = useState<Filter[]>([]);
  const [editorBreakevenMultiple, setEditorBreakevenMultiple] = useState<string>('1');
  const [isEditorSaving, setIsEditorSaving] = useState(false);
  const [removeConfirm, setRemoveConfirm] = useState<{ columnType: ColType; dependents: ColType[] } | null>(null);

  const availableValueTypes = useMemo(() => getValueTypesForSummary('firm'), []);
  const valueTypeById = useMemo(
    () => new Map(availableValueTypes.map(valueType => [valueType.id, valueType])),
    [availableValueTypes]
  );
  const requiredColumnIds = useMemo(
    () => availableValueTypes.filter(valueType => valueType.metadata?.isRequired).map(valueType => valueType.id),
    [availableValueTypes]
  );
  const addableValueTypes = useMemo(
    () => availableValueTypes.filter(valueType => !editorColumns.includes(valueType.id)),
    [availableValueTypes, editorColumns]
  );
  const hasBreakevenColumn = useMemo(
    () => editorColumns.includes(COLUMN_TYPES.BREAKEVEN_EXIT_EQUITY_VALUE),
    [editorColumns]
  );
  const breakevenMultipleValue = Number(editorBreakevenMultiple);
  const isBreakevenMultipleInvalid =
    hasBreakevenColumn && (!Number.isFinite(breakevenMultipleValue) || breakevenMultipleValue <= 0);
  const filterOptions = useMemo(
    () =>
      [
        { value: '=' as FilterOp, label: 'Equals' },
        { value: '!=' as FilterOp, label: 'Not equal' },
        { value: '>' as FilterOp, label: 'Greater than' },
        { value: '>=' as FilterOp, label: 'Greater or equal' },
        { value: '<' as FilterOp, label: 'Less than' },
        { value: '<=' as FilterOp, label: 'Less or equal' },
        { value: 'contains' as FilterOp, label: 'Contains' },
      ],
    []
  );

  const handleMenuClose = useCallback(() => setCurrentTabMenu(null), []);

  const handleMenuOpen = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      setCurrentTabMenu(event.currentTarget as HTMLSpanElement);
    },
    []
  );

  // Action handlers
  const handleCreateDefinition = useCallback(async () => {
    if (isSaving) {
      return;
    }

    if (activeDefinition) {
      const existingNames = orderedDefinitions.map(d => d.name);
      const clonedDefinition = await cloneDefinition(activeDefinition.id, generateCloneName(activeDefinition.name, existingNames));
      if (clonedDefinition) {
        setActiveDefinition(clonedDefinition.id);
        setApproachQueryParam(`summary-definition-${clonedDefinition.id}`);
      }
      return;
    }

    const newDefinition = await createDefinition('New View');
    if (newDefinition) {
      setActiveDefinition(newDefinition.id);
      setApproachQueryParam(`summary-definition-${newDefinition.id}`);
    }
  }, [activeDefinition, cloneDefinition, createDefinition, isSaving, orderedDefinitions, setActiveDefinition, setApproachQueryParam]);

  const handleCloneDefinition = useCallback(async (definitionId: number) => {
    if (isSaving) {
      return;
    }
    const definitionToClone = orderedDefinitions.find(d => d.id === definitionId);
    if (!definitionToClone) {
      return;
    }
    handleMenuClose();
    const existingNames = orderedDefinitions.map(d => d.name);
    const clonedDefinition = await cloneDefinition(definitionId, generateCloneName(definitionToClone.name, existingNames));
    if (clonedDefinition) {
      setActiveDefinition(clonedDefinition.id);
      setApproachQueryParam(`summary-definition-${clonedDefinition.id}`);
    }
  }, [
    orderedDefinitions,
    cloneDefinition,
    isSaving,
    setActiveDefinition,
    handleMenuClose,
    setApproachQueryParam,
  ]);

  const handleDeleteDefinition = useCallback(async (definitionId: number) => {
    handleMenuClose();
    await deleteDefinition(definitionId);
  }, [deleteDefinition, handleMenuClose]);

  const normalizeDefinition = useCallback((definition?: SpreadsheetDefinition): SpreadsheetDefinition => {
    const fallbackColumns = Object.fromEntries(DEFAULT_FIRM_SUMMARY_COLUMNS.map((col, idx) => [idx, col]));
    return {
      columns: definition?.columns ?? fallbackColumns,
      sortKeys: definition?.sortKeys ?? [],
      groupBy: definition?.groupBy ?? 'company',
      filters: definition?.filters ?? [],
      filterExclusions: definition?.filterExclusions ?? [],
      breakevenMultiple: definition?.breakevenMultiple ?? 1,
    };
  }, []);

  const getOrderedColumnTypes = useCallback((definition?: SpreadsheetDefinition): ColType[] => {
    if (!definition?.columns || Object.keys(definition.columns).length === 0) {
      return DEFAULT_FIRM_SUMMARY_COLUMNS;
    }

    return Object.entries(definition.columns)
      .sort(([a], [b]) => Number(a) - Number(b))
      .map(([, colType]) => colType);
  }, []);

  const canEditDefinition = useCallback(
    (definition?: SummaryDefinition | typeof orderedDefinitions[number] | null) => {
      if (!definition) {
        return false;
      }
      if ('isGlobal' in definition && definition.isGlobal) {
        return Boolean(definition.firm || definition.fund);
      }
      return true;
    }, []);

  const openColumnEditor = useCallback(() => {
    if (!isCustomViewsEnabled || !activeDefinition || !canEditDefinition(activeDefinition)) {
      return;
    }

    const currentDefinition = normalizeDefinition(
      activeDefinition.spreadsheet_definition ?? activeDefinition.spreadsheetDefinition
    );
    const orderedColumns = getOrderedColumnTypes(currentDefinition);
    const withRequired = [...orderedColumns];

    requiredColumnIds.forEach(requiredId => {
      if (!withRequired.includes(requiredId)) {
        withRequired.unshift(requiredId);
      }
    });

    setEditorColumns(withRequired);
    setEditorGroupBy(currentDefinition.groupBy ?? 'company');
    setEditorSortKeys(currentDefinition.sortKeys ?? []);
    setEditorFilters(currentDefinition.filters ?? []);
    setEditorBreakevenMultiple(`${currentDefinition.breakevenMultiple ?? 1}`);
    setColumnToAdd('');
    setEditorName(activeDefinition.name ?? '');
    setIsColumnEditorOpen(true);
  }, [activeDefinition, canEditDefinition, getOrderedColumnTypes, isCustomViewsEnabled, normalizeDefinition, requiredColumnIds]);

  const closeColumnEditor = useCallback(() => {
    setIsColumnEditorOpen(false);
    setEditorName('');
    setEditorColumns([]);
    setColumnToAdd('');
    setEditorGroupBy('company');
    setEditorSortKeys([]);
    setEditorFilters([]);
    setEditorBreakevenMultiple('1');
    setIsEditorSaving(false);
    setRemoveConfirm(null);
  }, []);

  const handleAddColumn = useCallback(() => {
    if (!columnToAdd || !SUPPORTED_FIRM_SUMMARY_COLUMN_TYPES.has(columnToAdd)) {
      return;
    }

    // Use the dependency resolution utility to get all required columns
    const tempDefinition: SpreadsheetDefinition = {
      columns: Object.fromEntries(editorColumns.map((col, idx) => [idx, col])),
      sortKeys: [],
      groupBy: 'company',
      filters: [],
      filterExclusions: [],
    };

    const updatedDefinition = addColumnWithDependencies(tempDefinition, columnToAdd);
    const newColumns = Object.entries(updatedDefinition.columns)
      .sort(([a], [b]) => Number(a) - Number(b))
      .map(([, colType]) => colType);

    setEditorColumns(newColumns);
    setColumnToAdd('');
  }, [columnToAdd, editorColumns]);

  const columnIds = useMemo(() => editorColumns.map((_, i) => `column-${i}`), [editorColumns]);
  const sortIds = useMemo(() => editorSortKeys.map((_, i) => `sort-${i}`), [editorSortKeys]);
  const filterIds = useMemo(() => editorFilters.map((_, i) => `filter-${i}`), [editorFilters]);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveDragId(String(event.active.id));
  }, []);

  const handleReorderColumns = useCallback(
    (event: DragEndEvent) => {
      setActiveDragId(null);
      const { active, over } = event;
      if (!over || active.id === over.id) {
        return;
      }
      const oldIndex = columnIds.indexOf(String(active.id));
      const newIndex = columnIds.indexOf(String(over.id));
      if (oldIndex === -1 || newIndex === -1) {
        return;
      }
      setEditorColumns(prev => arrayMove(prev, oldIndex, newIndex));
    },
    [columnIds]
  );

  const handleReorderSortKeys = useCallback(
    (event: DragEndEvent) => {
      setActiveDragId(null);
      const { active, over } = event;
      if (!over || active.id === over.id) {
        return;
      }
      const oldIndex = sortIds.indexOf(String(active.id));
      const newIndex = sortIds.indexOf(String(over.id));
      if (oldIndex === -1 || newIndex === -1) {
        return;
      }
      setEditorSortKeys(prev => arrayMove(prev, oldIndex, newIndex));
    },
    [sortIds]
  );

  const handleReorderFilters = useCallback(
    (event: DragEndEvent) => {
      setActiveDragId(null);
      const { active, over } = event;
      if (!over || active.id === over.id) {
        return;
      }
      const oldIndex = filterIds.indexOf(String(active.id));
      const newIndex = filterIds.indexOf(String(over.id));
      if (oldIndex === -1 || newIndex === -1) {
        return;
      }
      setEditorFilters(prev => arrayMove(prev, oldIndex, newIndex));
    },
    [filterIds]
  );

  const handleRemoveColumn = useCallback(
    (columnType: ColType) => {
      if (requiredColumnIds.includes(columnType)) {
        return;
      }

      const draftDefinition = normalizeDefinition({
        columns: Object.fromEntries(editorColumns.map((col, idx) => [idx, col])),
        sortKeys: [],
        groupBy: 'company',
        filters: [],
        filterExclusions: [],
      });
      const dependents = getAffectedColumns(draftDefinition, columnType);
      const affected = dependents.length > 0 ? [columnType, ...dependents] : [columnType];

      if (dependents.length > 0) {
        setRemoveConfirm({ columnType, dependents });
        return;
      }

      setEditorColumns(prev => prev.filter(col => !affected.includes(col)));
    },
    [editorColumns, normalizeDefinition, requiredColumnIds]
  );

  const handleAddSortKey = useCallback(() => {
    const defaultColumn = editorColumns[0];
    if (!defaultColumn) {
      return;
    }
    setEditorSortKeys(prev => [...prev, [defaultColumn, 'asc']]);
  }, [editorColumns]);

  const handleUpdateSortColumn = useCallback((index: number, column: ColType) => {
    setEditorSortKeys(prev => prev.map((sortKey, idx) => (idx === index ? [column, sortKey[1]] : sortKey)));
  }, []);

  const handleUpdateSortDirection = useCallback((index: number, direction: SortKey[1]) => {
    setEditorSortKeys(prev => prev.map((sortKey, idx) => (idx === index ? [sortKey[0], direction] : sortKey)));
  }, []);

  const handleRemoveSortKey = useCallback((index: number) => {
    setEditorSortKeys(prev => prev.filter((_, idx) => idx !== index));
  }, []);

  const handleAddFilter = useCallback(() => {
    const defaultColumn = editorColumns[0];
    if (!defaultColumn) {
      return;
    }
    setEditorFilters(prev => [...prev, [defaultColumn, ['=', '']] as Filter]);
  }, [editorColumns]);

  const handleUpdateFilterColumn = useCallback((index: number, column: ColType) => {
    setEditorFilters(prev => prev.map((filter, idx) => (idx === index ? [column, filter[1]] : filter)));
  }, []);

  const handleUpdateFilterOp = useCallback((index: number, op: FilterOp) => {
    setEditorFilters(prev => prev.map((filter, idx) => (idx === index ? [filter[0], [op, filter[1][1]]] : filter)));
  }, []);

  const handleUpdateFilterValue = useCallback((index: number, value: string | number) => {
    setEditorFilters(prev => prev.map((filter, idx) => (idx === index ? [filter[0], [filter[1][0], value]] : filter)));
  }, []);

  const handleRemoveFilter = useCallback((index: number) => {
    setEditorFilters(prev => prev.filter((_, idx) => idx !== index));
  }, []);

  const handleConfirmRemove = useCallback(() => {
    if (!removeConfirm) {
      return;
    }

    const affected = [removeConfirm.columnType, ...removeConfirm.dependents];
    setEditorColumns(prev => prev.filter(col => !affected.includes(col)));
    setRemoveConfirm(null);
  }, [removeConfirm]);

  const handleCancelRemove = useCallback(() => {
    setRemoveConfirm(null);
  }, []);

  const handleSaveColumns = useCallback(async () => {
    if (!activeDefinition || isEditorSaving || isBreakevenMultipleInvalid) {
      return;
    }

    const baseDefinition = normalizeDefinition(
      activeDefinition.spreadsheet_definition ?? activeDefinition.spreadsheetDefinition
    );
    const validColumns = new Set(editorColumns);
    const sanitizedSortKeys = editorSortKeys.filter(([col]) => validColumns.has(col));
    const sanitizedFilters = editorFilters.filter(
      ([col, [, value]]) => validColumns.has(col) && value !== null && typeof value !== 'undefined' && `${value}` !== ''
    );
    const nextDefinition: SpreadsheetDefinition = {
      ...baseDefinition,
      columns: Object.fromEntries(editorColumns.map((col, idx) => [idx, col])),
      groupBy: editorGroupBy,
      sortKeys: sanitizedSortKeys,
      filters: sanitizedFilters,
      breakevenMultiple: hasBreakevenColumn ? breakevenMultipleValue : baseDefinition.breakevenMultiple ?? 1,
    };

    try {
      setIsEditorSaving(true);
      const trimmedName = editorName.trim();
      if (!trimmedName) {
        return;
      }
      await updateDefinition(activeDefinition.id, {
        name: trimmedName,
        spreadsheet_definition: nextDefinition,
      });
      await refetch();
      closeColumnEditor();
    } finally {
      setIsEditorSaving(false);
    }
  }, [
    activeDefinition,
    closeColumnEditor,
    editorColumns,
    editorFilters,
    editorGroupBy,
    editorName,
    isEditorSaving,
    isBreakevenMultipleInvalid,
    normalizeDefinition,
    refetch,
    editorSortKeys,
    hasBreakevenColumn,
    breakevenMultipleValue,
    updateDefinition,
  ]);

  // Build menu items for the active tab
  const buildMenuItems = useCallback(
    (definition: typeof orderedDefinitions[number]) => {
      if (!isCustomViewsEnabled) {
        return [];
      }

      const items = [
        <MenuItem key="clone" onClick={() => handleCloneDefinition(definition.id)} disabled={isSaving}>
          <ListItemIcon>
            <ContentCopy fontSize="small" />
          </ListItemIcon>
          <ListItemText>Clone</ListItemText>
        </MenuItem>,
      ];

      // Only allow deletion for non-global definitions
      if (canEditDefinition(definition)) {
        items.unshift(
          <MenuItem
            key="edit-columns"
            onClick={openColumnEditor}
            disabled={isSaving || isEditorSaving}>
            <ListItemIcon>
              <ViewColumn fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit Summary</ListItemText>
          </MenuItem>
        );
        items.push(
          <MenuItem key="delete" onClick={() => handleDeleteDefinition(definition.id)} disabled={isSaving}>
            <ListItemIcon>
              <Delete fontSize="small" />
            </ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        );
      }

      return items;
    },
    [
      canEditDefinition,
      handleCloneDefinition,
      handleDeleteDefinition,
      isCustomViewsEnabled,
      isEditorSaving,
      isSaving,
      openColumnEditor,
    ]
  );

  const tabs = useMemo((): Tab[] => {
    // Build the "+" action tab for creating new definitions (only when custom views enabled)
    const addTab: Tab = {
      id: 'add-new-definition',
      label: (
        <span
          role="button"
          tabIndex={0}
          onMouseDown={() => {
            addTabClickedRef.current = true;
          }}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              addTabClickedRef.current = true;
            }
          }}>
          +
        </span>
      ),
      isActionTab: true,
      content: null,
    };

    if (!isCustomViewsEnabled) {
      return orderedDefinitions.length
        ? orderedDefinitions.map(definition => ({
          ...firmSummaryTab,
          id: `summary-definition-${definition.id}`,
          label: definition.name,
          menuItems: buildMenuItems(definition),
        }))
        : [firmSummaryTab];
    }

    if (!orderedDefinitions.length) {
      return [firmSummaryTab, addTab];
    }

    const definitionTabs: Tab[] = orderedDefinitions.map(definition => ({
      ...firmSummaryTab,
      id: `summary-definition-${definition.id}`,
      label: definition.name,
      menuItems: buildMenuItems(definition),
    }));

    // Add "+" tab at the end
    definitionTabs.push(addTab);

    return definitionTabs;
  }, [firmSummaryTab, orderedDefinitions, buildMenuItems, isCustomViewsEnabled]);

  const handleActiveTabChange = useCallback(
    (tabToActivate?: Tab) => {
      if (!tabToActivate?.id) {
        return;
      }

      // Handle the "+" tab — only if explicitly clicked, not from reflow
      if (tabToActivate.id === 'add-new-definition') {
        if (!addTabClickedRef.current) {
          return;
        }
        addTabClickedRef.current = false;
        closeColumnEditor();
        handleCreateDefinition();
        return;
      }

      const id = tabToActivate.id.replace('summary-definition-', '');
      const parsedId = id ? Number(id) : NaN;

      if (!Number.isNaN(parsedId) && parsedId !== activeDefinition?.id) {
        closeColumnEditor();
        setActiveDefinition(parsedId);
      } else if (Number.isNaN(parsedId)) {
        closeColumnEditor();
        setActiveDefinition(null);
      }
      // If parsedId === activeDefinition?.id, do nothing (tab array updated but same tab is active)
    },
    [activeDefinition?.id, closeColumnEditor, handleCreateDefinition, setActiveDefinition]
  );

  return (
    <>
      <TabsNavigation
        isDisabled={isLoading || isSaving}
        handleMenuClose={handleMenuClose}
        handleMenuOpen={handleMenuOpen}
        menuAnchorEl={currentTabMenu}
        onActiveTabChange={handleActiveTabChange}
        mountOnActive
        tabs={tabs}
        updateUrlOnTabChange>
        {/* Pending decisions */}
        <FirmPendingDecisions />

        {/* Add company message */}
        {hasNoCompanies && <NoCompanyMessage />}

        {/* Add investment message */}
        {!hasNoCompanies && hasNoInvestments && <NoInformationMessage />}
      </TabsNavigation>

      <Dialog
        maxWidth="sm"
        fullWidth
        open={isColumnEditorOpen}
        onClose={closeColumnEditor}>
        <DialogTitle>Edit Summary</DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle2" sx={{ marginBottom: 1 }}>
            Name
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={editorName}
            onChange={event => setEditorName(event.target.value)}
            disabled={isEditorSaving}
            error={editorName.trim().length === 0}
            helperText={editorName.trim().length === 0 ? 'Name is required.' : ' '}
            sx={{ marginBottom: 2 }}
          />

          <Typography variant="subtitle2" sx={{ marginBottom: 1 }}>
            Grouping
          </Typography>
          <FormControl fullWidth size="small" variant="outlined">
            <InputLabel id="summary-groupby-label">Group by</InputLabel>
            <Select
              labelId="summary-groupby-label"
              label="Group by"
              value={editorGroupBy}
              onChange={event => setEditorGroupBy(event.target.value as SummaryGroupBy)}
              disabled={isEditorSaving}>
              <MenuItem value="company">Company</MenuItem>
              <MenuItem value="fund">Fund</MenuItem>
            </Select>
            <FormHelperText>Choose whether rows expand by company or fund.</FormHelperText>
          </FormControl>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" sx={{ marginBottom: 1 }}>
            Columns
          </Typography>
          <FormControl fullWidth size="small" variant="outlined" sx={{ marginBottom: 2 }}>
            <InputLabel id="summary-columns-add-label">Add column</InputLabel>
            <Select
              labelId="summary-columns-add-label"
              label="Add column"
              value={columnToAdd}
              onChange={event => setColumnToAdd(event.target.value as ColType)}
              disabled={isEditorSaving}>
              {addableValueTypes.map(valueType => {
                const isSupported = SUPPORTED_FIRM_SUMMARY_COLUMN_TYPES.has(valueType.id);
                return (
                  <MenuItem
                    key={valueType.id}
                    value={valueType.id}
                    disabled={!isSupported}
                    sx={!isSupported ? { color: 'text.secondary' } : undefined}>
                    {valueType.displayName}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>

          <Button
            startIcon={<Add />}
            variant="outlined"
            onClick={handleAddColumn}
            disabled={!columnToAdd || isEditorSaving}
            sx={{ marginBottom: 2 }}>
            Add Column
          </Button>

          <List dense>
            <DndContext
              sensors={sortableSensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleReorderColumns}>
              <SortableContext items={columnIds} strategy={verticalListSortingStrategy}>
                {editorColumns.map((columnType, index) => {
                  const valueType = valueTypeById.get(columnType);
                  const displayName = valueType?.displayName ?? columnType;
                  const isRequired = requiredColumnIds.includes(columnType);

                  return (
                    <SortableListItem
                      key={`${columnType}-${index}`}
                      id={`column-${index}`}
                      disabled={isEditorSaving || isRequired}
                      secondaryAction={
                        <IconButton
                          edge="end"
                          onClick={() => handleRemoveColumn(columnType)}
                          disabled={isRequired || isEditorSaving}
                          aria-label="Remove column">
                          <Delete fontSize="small" />
                        </IconButton>
                      }>
                      <ListItemText
                        primary={displayName}
                        secondary={isRequired ? 'Required' : valueType?.metadata?.category}
                      />
                    </SortableListItem>
                  );
                })}
              </SortableContext>
              <DragOverlay dropAnimation={null}>
                {activeDragId?.startsWith('column-') ? (
                  (() => {
                    const idx = parseInt(activeDragId.replace('column-', ''), 10);
                    const columnType = editorColumns[idx];
                    const valueType = valueTypeById.get(columnType);
                    const displayName = valueType?.displayName ?? columnType;
                    return (
                      <Paper
                        elevation={8}
                        sx={{
                          px: 2,
                          py: 1.5,
                          cursor: 'grabbing',
                          borderRadius: 1,
                          minWidth: 200,
                          transform: 'rotate(2deg)',
                          boxShadow: 3,
                        }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <DragIndicator fontSize="small" color="action" />
                          <Typography variant="body2" fontWeight={500}>
                            {displayName}
                          </Typography>
                        </Stack>
                      </Paper>
                    );
                  })()
                ) : null}
              </DragOverlay>
            </DndContext>
          </List>

          {hasBreakevenColumn ? (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" sx={{ marginBottom: 1 }}>
                Breakeven Multiple
              </Typography>
              <TextField
                fullWidth
                size="small"
                type="number"
                value={editorBreakevenMultiple}
                onChange={event => setEditorBreakevenMultiple(event.target.value)}
                disabled={isEditorSaving}
                error={isBreakevenMultipleInvalid}
                helperText={
                  isBreakevenMultipleInvalid
                    ? 'Enter a value greater than 0.'
                    : 'Used to calculate the Breakeven Exit Equity Value.'
                }
                inputProps={{ min: 0, step: 0.01 }}
              />
            </>
          ) : null}

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" sx={{ marginBottom: 1 }}>
            Sort order
          </Typography>
          <Button
            startIcon={<Add />}
            variant="outlined"
            onClick={handleAddSortKey}
            disabled={isEditorSaving || editorColumns.length === 0}
            sx={{ marginBottom: 2 }}>
            Add Sort Key
          </Button>

          <List dense>
            <DndContext
              sensors={sortableSensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleReorderSortKeys}>
              <SortableContext items={sortIds} strategy={verticalListSortingStrategy}>
                {editorSortKeys.map(([column, direction], index) => (
                  <SortableListItem key={`sort-${index}`} id={`sort-${index}`} disabled={isEditorSaving}>
                    <Stack
                      alignItems="center"
                      direction="row"
                      spacing={1}
                      sx={{ width: '100%', minWidth: 0 }}>
                      <FormControl fullWidth size="small" sx={{ minWidth: 160 }}>
                        <InputLabel id={`sort-column-${index}`}>Column</InputLabel>
                        <Select
                          labelId={`sort-column-${index}`}
                          label="Column"
                          value={column}
                          onChange={event => handleUpdateSortColumn(index, event.target.value as ColType)}
                          disabled={isEditorSaving}>
                          {editorColumns.map(col => (
                            <MenuItem key={`sort-col-${col}`} value={col}>
                              {valueTypeById.get(col)?.displayName ?? col}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <FormControl fullWidth size="small" sx={{ minWidth: 140 }}>
                        <InputLabel id={`sort-direction-${index}`}>Direction</InputLabel>
                        <Select
                          labelId={`sort-direction-${index}`}
                          label="Direction"
                          value={direction}
                          onChange={event => handleUpdateSortDirection(index, event.target.value as SortKey[1])}
                          disabled={isEditorSaving}>
                          <MenuItem value="asc">Ascending</MenuItem>
                          <MenuItem value="desc">Descending</MenuItem>
                        </Select>
                      </FormControl>

                      <IconButton
                        onClick={() => handleRemoveSortKey(index)}
                        disabled={isEditorSaving}
                        aria-label="Remove sort key"
                        sx={{ flexShrink: 0 }}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Stack>
                  </SortableListItem>
                ))}
              </SortableContext>
              <DragOverlay dropAnimation={null}>
                {activeDragId?.startsWith('sort-') ? (
                  (() => {
                    const idx = parseInt(activeDragId.replace('sort-', ''), 10);
                    const [column, direction] = editorSortKeys[idx] ?? [];
                    const displayName = valueTypeById.get(column)?.displayName ?? column;
                    return (
                      <Paper
                        elevation={8}
                        sx={{
                          px: 2,
                          py: 1.5,
                          cursor: 'grabbing',
                          borderRadius: 1,
                          minWidth: 220,
                          transform: 'rotate(2deg)',
                          boxShadow: 3,
                        }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <DragIndicator fontSize="small" color="action" />
                          <Typography variant="body2" fontWeight={500}>
                            {displayName} ({direction === 'asc' ? 'Asc' : 'Desc'})
                          </Typography>
                        </Stack>
                      </Paper>
                    );
                  })()
                ) : null}
              </DragOverlay>
            </DndContext>
          </List>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" sx={{ marginBottom: 1 }}>
            Filters
          </Typography>
          <Button
            startIcon={<Add />}
            variant="outlined"
            onClick={handleAddFilter}
            disabled={isEditorSaving || editorColumns.length === 0}
            sx={{ marginBottom: 2 }}>
            Add Filter
          </Button>

          <List dense>
            <DndContext
              sensors={sortableSensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleReorderFilters}>
              <SortableContext items={filterIds} strategy={verticalListSortingStrategy}>
                {editorFilters.map(([column, [op, value]], index) => (
                  <SortableListItem
                    key={`filter-${index}`}
                    id={`filter-${index}`}
                    disabled={isEditorSaving}
                    secondaryAction={
                      <IconButton
                        edge="end"
                        onClick={() => handleRemoveFilter(index)}
                        disabled={isEditorSaving}
                        aria-label="Remove filter">
                        <Delete fontSize="small" />
                      </IconButton>
                    }>
                    <Stack alignItems="center" direction="row" spacing={1} sx={{ width: '100%' }}>
                      <FormControl fullWidth size="small" sx={{ width: 160 }}>
                        <InputLabel id={`filter-column-${index}`}>Column</InputLabel>
                        <Select
                          labelId={`filter-column-${index}`}
                          label="Column"
                          value={column}
                          onChange={event => handleUpdateFilterColumn(index, event.target.value as ColType)}
                          disabled={isEditorSaving}>
                          {editorColumns.map(col => (
                            <MenuItem key={`filter-col-${col}`} value={col}>
                              {valueTypeById.get(col)?.displayName ?? col}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <FormControl fullWidth size="small" sx={{ width: 130 }}>
                        <InputLabel id={`filter-op-${index}`}>Condition</InputLabel>
                        <Select
                          labelId={`filter-op-${index}`}
                          label="Condition"
                          value={op}
                          onChange={event => handleUpdateFilterOp(index, event.target.value as FilterOp)}
                          disabled={isEditorSaving}>
                          {filterOptions.map(option => (
                            <MenuItem key={`filter-op-${option.value}`} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <TextField
                        label="Value"
                        size="small"
                        value={value ?? ''}
                        onChange={event => handleUpdateFilterValue(index, event.target.value)}
                        disabled={isEditorSaving}
                        sx={{ width: 150 }}
                      />
                    </Stack>
                  </SortableListItem>
                ))}
              </SortableContext>
              <DragOverlay dropAnimation={null}>
                {activeDragId?.startsWith('filter-') ? (
                  (() => {
                    const idx = parseInt(activeDragId.replace('filter-', ''), 10);
                    const [column, [op, value]] = editorFilters[idx] ?? [];
                    const displayName = valueTypeById.get(column)?.displayName ?? column;
                    const opLabel = filterOptions.find(o => o.value === op)?.label ?? op;
                    return (
                      <Paper
                        elevation={8}
                        sx={{
                          px: 2,
                          py: 1.5,
                          cursor: 'grabbing',
                          borderRadius: 1,
                          minWidth: 240,
                          transform: 'rotate(2deg)',
                          boxShadow: 3,
                        }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <DragIndicator fontSize="small" color="action" />
                          <Typography variant="body2" fontWeight={500}>
                            {displayName} {opLabel} {String(value || '')}
                          </Typography>
                        </Stack>
                      </Paper>
                    );
                  })()
                ) : null}
              </DragOverlay>
            </DndContext>
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeColumnEditor} disabled={isEditorSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveColumns}
            variant="contained"
            disabled={
              isEditorSaving ||
              editorColumns.length === 0 ||
              editorName.trim().length === 0 ||
              isBreakevenMultipleInvalid
            }>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        maxWidth="xs"
        fullWidth
        open={Boolean(removeConfirm)}
        onClose={handleCancelRemove}>
        <DialogTitle>Remove dependent columns?</DialogTitle>
        <DialogContent dividers>
          {removeConfirm ? (
            <List dense>
              <ListItem>
                <ListItemText
                  primary={valueTypeById.get(removeConfirm.columnType)?.displayName ?? removeConfirm.columnType}
                  secondary="Selected column"
                />
              </ListItem>
              {removeConfirm.dependents.map(dependent => (
                <ListItem key={dependent}>
                  <ListItemText
                    primary={valueTypeById.get(dependent)?.displayName ?? dependent}
                    secondary="Dependent column"
                  />
                </ListItem>
              ))}
            </List>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelRemove}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleConfirmRemove}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const FirmSummary: FC = () => {
  const [storeValue] = useStore() as UseStoreValues;
  const { firmInfo } = getObjectValue(storeValue);
  const firmId = getObjectValue(firmInfo).id;
  const [, checkFeature] = useCheckFeatures();
  const isCustomViewsEnabled = checkFeature(FIRM_SUMMARY_CUSTOM_VIEWS_ENABLER);

  if (!firmId) {
    return null;
  }

  return (
    <SummaryDefinitionsProvider entityId={firmId} summaryType="firm" fetchEnabled={isCustomViewsEnabled}>
      <FirmSummaryTabs />
    </SummaryDefinitionsProvider>
  );
};

export default FirmSummary;
